import React from 'react';
import styled, { css } from 'styled-components';
import { GenericItemDataSheet } from '@marty-js/api-sdk/types';
import { HeadingTwo } from '../../atoms/h2';
import { TranslationType, useTranslation } from '../../utils/translation';
import { DEVICE_SIZE, mq } from '../../utils/mq';
import TableMultiple from '../table-multiple';
import TableMultipleComparative from '../table-multiple-comparative';
import { extractTopSpecs } from '../../utils/datasheet';

interface ClubicContainerProps {
  cell: any;
}

const TechnicalWrapper = styled.div`
  &.mod-comparative {
    max-width: calc(100vw - 40px);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: var(--spacer-s) var(--spacer-l);

      &.mod-comparative {
        margin: var(--spacer) 0;
        max-width: 100%;

        h2 {
          padding: 0 var(--spacer-l);
        }
      }
    `,
  )}
`;

export const processComparativeData = (data: any) => {
  const criteria = [{ label: 'Note', key: 'globalScore' }];
  const values = data?.tagDataSheetsWithItemInfo.map((item: any) => {
    const topSpecs = extractTopSpecs(item?.datasheet?.groups || [], criteria);

    return {
      ...topSpecs,
      ...item,
    };
  });

  return { criteria, values };
};

const processSimpleData = (dataSheets: GenericItemDataSheet[], t: TranslationType) => {
  return dataSheets.flatMap((dataSheet) => {
    const topSpecAttributes = extractTopSpecs(dataSheet?.groups || [], []);

    const topSpecData = Object.entries(topSpecAttributes).map(([key, value]) => {
      const attribute = dataSheet.groups?.flatMap((group) => group.attributes)?.find((attr) => attr.shortSlug === key);

      return {
        label: attribute?.name || key,
        value,
      };
    });

    const groups = dataSheet?.groups?.map((group) => ({
      title: group?.name,
      table: {
        data: group?.attributes?.map((attribute) => ({
          label: attribute?.name,
          value: attribute?.value,
        })),
      },
    }));

    return [
      {
        title: t('design.product.resume_datasheet'),
        table: {
          data: topSpecData,
        },
      },
      ...groups,
    ];
  });
};

const TechnicalBlockArticle = ({ cell }: ClubicContainerProps) => {
  const t = useTranslation();
  const data = cell.content.state?.data;

  if (!data) {
    return null;
  }

  const isComparative = data?.tagDataSheetsWithItemInfo?.length > 1;
  const { criteria, values } = isComparative
    ? processComparativeData(data)
    : {
        criteria: [],
        values: processSimpleData(
          data.tagDataSheetsWithItemInfo[0].datasheet ? [data.tagDataSheetsWithItemInfo[0].datasheet] : [],
          t,
        ),
      };

  return (
    <TechnicalWrapper className={isComparative ? 'mod-comparative' : ''}>
      {cell.content.state?.hasTitle && (
        <HeadingTwo>
          {t('design.product.datasheet')} {data?.itemTitle}
        </HeadingTwo>
      )}
      {isComparative ? (
        <TableMultipleComparative criteria={criteria} values={values} />
      ) : (
        <TableMultiple data={values} />
      )}
    </TechnicalWrapper>
  );
};

export default TechnicalBlockArticle;
